import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Fade,
} from '@mui/material';
import {
  CheckCircle,
  Group,
  Visibility,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface LotCreatedData {
  data: {
    _id?: string;
    _key?: string;
    _rev?: string;
  };
  resumo?: string;
  url?: string;
}

export interface LotCreatedSuccessViewProps {
  lotCreatedData: LotCreatedData;
  onViewDetails?: () => void;
  onCreateAnother?: () => void;
}

const LotCreatedSuccessView: React.FC<LotCreatedSuccessViewProps> = ({
  lotCreatedData,
  onViewDetails,
  onCreateAnother,
}) => {
  const { t } = useTranslation(['farm', 'common']);
  const { data, resumo } = lotCreatedData;

  return (
    <Box sx={{ 
      p: 4, 
      display: 'flex', 
      flexDirection: 'column', 
      alignItems: 'center', 
      justifyContent: 'center',
      minHeight: '400px',
      textAlign: 'center'
    }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          {/* Success Icon */}
          <Avatar
            sx={{
              width: 120,
              height: 120,
              bgcolor: DESIGN_TOKENS.colors.brand.primary,
              mb: 3,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.brand.surface}`,
            }}
          >
            <CheckCircle sx={{ fontSize: '4rem', color: 'white' }} />
          </Avatar>

          {/* Success Message */}
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              color: DESIGN_TOKENS.colors.brand.primary,
              mb: 2
            }}
          >
            {t('farm:lotCreatedSuccess.title')}
          </Typography>

          <Typography 
            variant="h6" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.primary,
              mb: 1,
              fontWeight: 600
            }}
          >
            {resumo || t('farm:lotCreatedSuccess.subtitle')}
          </Typography>

          {/* Lot Details Card */}
          <Card sx={{ 
            mt: 3, 
            mb: 4, 
            minWidth: 300,
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            border: `2px solid ${DESIGN_TOKENS.colors.brand.surface}`
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Group sx={{ color: DESIGN_TOKENS.colors.organization.farm, fontSize: '2rem' }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.organization.farm }}>
                    {t('farm:lotCreatedSuccess.newLot')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('farm:lotCreatedSuccess.createdNow')}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('farm:lotCreatedSuccess.lotId')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {data._key || 'N/A'}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                    {t('farm:lotCreatedSuccess.document')}:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {data._id || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Action Buttons - Removidos conforme solicitado */}

          {/* Additional Info */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: DESIGN_TOKENS.colors.text.secondary,
              mt: 3,
              fontStyle: 'italic'
            }}
          >
            {t('farm:lotCreatedSuccess.additionalInfo')}
          </Typography>
        </Box>
      </Fade>
    </Box>
  );
};

export default LotCreatedSuccessView;